<template>
  <div
    v-if="isVisible"
    @blur="closePopup"
    tabindex="0"
    class="d-flex flex-column popup mb-2"
    :style="{ height: popupHeight }"
  >
    <div v-if="showCabel" class="box">
      <div class="d-flex flex-row align-items-center mb-2">
        <img src="@/assets/svg/placer.svg" class="icon" />
        <p class="NKT-P1-medium ms-2 mb-0">Kilometer Point:</p>
        <p class="NKT-P1-medium ms-2 mb-0">{{ kp.toFixed(2) }}</p>
        <p class="NKT-P1 ms-2 mb-0 ms-grey">({{ cableLength?.toFixed(2) }} km)</p>
      </div>

      <div class="NKT-P1 w-100 sensor-rows">
        <div class="flex-item">DoB:</div>
        <div class="flex-item">{{ formattedDoB }} m</div>
      </div>
      <div class="NKT-P1 w-100 sensor-rows">
        <div class="flex-item">Average temp:</div>
        <div class="flex-item">{{ formattedDts }}&deg;</div>
      </div>
      <div class="NKT-P1 w-100 sensor-rows">
        <div class="flex-item">DAS:</div>
        <div class="flex-item">{{ das }} dB</div>
      </div>
      <div class="d-flex flex-row mt-3">
        <p class="active-alarms mb-0">{{ noOfAlarms }} active alarms.</p>
      </div>
    </div>

    <div v-if="showVessel && vesselInfo !== undefined" class="box">
      <div class="d-flex flex-row align-items-center mb-3">
        <img src="@/assets/svg/placer.svg" class="icon" />
        <p class="NKT-P1-medium ms-2 mb-0">Position:</p>
        <p class="NKT-P1-medium ms-2 mb-0">
          {{ vesselInfo?.latitude?.toFixed(2) }}, {{ vesselInfo?.longitude?.toFixed(2) }}
        </p>
      </div>

      <div class="NKT-P1 w-100 sensor-rows">
        <div class="flex-item">Name:</div>
        <div class="flex-item truncate" :title="vesselInfo?.vesselTypeName">
          {{ vesselInfo?.vesselName }}
        </div>
      </div>

      <div class="NKT-P1 w-100 sensor-rows">
        <div class="flex-item">Type:</div>
        <div class="flex-item truncate" :title="vesselInfo?.vesselTypeName">
          {{ vesselInfo?.vesselTypeName }}
        </div>
      </div>

      <div class="NKT-P1 w-100 sensor-rows">
        <div class="flex-item">MMSI:</div>
        <div class="flex-item">{{ vesselInfo?.mmsi }}</div>
      </div>

      <div class="NKT-P1 w-100 sensor-rows">
        <div class="flex-item">Speed Over Ground:</div>
        <div class="flex-item">{{ vesselInfo?.speedOverGround?.toFixed(2) ?? '-' }}</div>
      </div>

      <div class="NKT-P1 w-100 sensor-rows">
        <div class="flex-item">Course Over Ground:</div>
        <div class="flex-item">{{ vesselInfo?.courseOverGround?.toFixed(2) ?? '-' }}</div>
      </div>

      <div class="NKT-P1 w-100 sensor-rows">
        <div class="flex-item">Radio Call Signal:</div>
        <div class="flex-item">{{ vesselInfo?.radioCallSign }}</div>
      </div>

      <div class="NKT-P1 w-100 sensor-rows">
        <div class="flex-item">Destination:</div>
        <div class="flex-item">
          {{ vesselInfo?.destination?.trim() ? vesselInfo.destination : 'Not known' }}
        </div>
      </div>
    </div>

    <div v-if="showVessel && vesselInfo === undefined" class="box">
      <DataLoading></DataLoading>
    </div>

    <img class="icon pop-up-icon" id="popup-icon" src="@/assets/svg/popup.svg" />
  </div>
</template>

<script setup lang="ts">
import {
  CableVessel,
  VesselClient,
  VesselInfo,
  VesselInfoRequest
} from '@/server/MakeSenseApp-backend'
import { usePopupStore } from '@/stores/PopupStore'
import { useProjectStore } from '@/stores/ProjectStore'
import { requiredInject } from '@/utilities/requiredInject'
import { computed, defineProps, nextTick, ref, watch } from 'vue'
import DataLoading from '../general/loading/DataLoading.vue'

const popupStore = usePopupStore()
const projectStore = useProjectStore()

defineProps({
  cableLength: Number
})

var client = requiredInject<VesselClient>('VesselClient')

const isVisible = computed(() => popupStore.isActive)

const showCabel = computed(() => popupStore.showCabel)

const showVessel = computed(() => popupStore.showVessel)
const vesselData = computed(() => popupStore.vesselData)

const vesselInfo = ref<VesselInfo | undefined>(undefined)

const popupHeight = computed(() => {
  return showCabel.value ? '160px' : showVessel.value ? '225px' : 'auto'
})

watch(vesselData, (newValue: CableVessel | undefined) => {
  vesselInfo.value = undefined

  if (newValue !== undefined) {
    client
      .vesselInfo(
        projectStore.projectId,
        new VesselInfoRequest({
          mmsi: newValue?.mmsi,
          timestamp: newValue.timestamp
        })
      )
      .then((response) => {
        vesselInfo.value = response
      })
  }
})

function closePopup() {
  popupStore.closePopup()
}

// watch to focus on the popup when it becomes visible
watch(isVisible, () => {
  if (isVisible.value) {
    nextTick(() => {
      const popupElement = document.getElementById('popup')
      popupElement?.focus()
    })
  }
})

const das = computed(() => popupStore.das)
const dts = computed(() => popupStore.dts)
const doB = computed(() => popupStore.doB)
const kp = computed(() => popupStore.kp)
const noOfAlarms = computed(() => popupStore.alarms!.length)
const formattedDoB = ref('')
const formattedDts = ref('')

watch(
  () => [doB.value, dts.value],
  ([newDoB, newDts]) => {
    formattedDoB.value = newDoB != null ? newDoB.toFixed(2) : ''
    formattedDts.value = newDts != null ? newDts.toFixed(2) : ''
  }
)
</script>

<style scoped lang="scss">
@use '@/styles/ms-color-styles.scss' as color;

.box {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
}

.popup {
  width: 380px;
  position: absolute;
  padding: 12px;
  border-radius: var(--border-radius-small-tryout, 4px);
  background: color.$color-white;
  box-shadow: 0px 0px 0.5px 0px rgba(66, 71, 76, 0.32), 0px 4px 8px 0px rgba(66, 71, 76, 0.05);
  outline: none !important;
}

.active-alarms {
  color: color.$color-dark-grey;
  font-family: Roboto;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
}

.sensor-rows {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 2px 0px;
}

.flex-item {
  flex: 1;
}

.pop-up-icon {
  position: absolute;
  right: 42px;
  bottom: -15px;
}

.truncate {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
